import { useRef, useState } from "react"

import { AssociateProductType } from "../../../types"

import { idsToTableSelectedRowFormat } from "@/components/Tables/Aeros/utils"
import type { CompetitorProduct, Product } from "@/graphql/codegen/graphql"
import { type AssociatedProduct, getProductId } from "@/utils/equivalentProducts"

interface UseProductSelectionsParams {
  associatedProductIds: number[]
  associatedCompetitorIds: string[]
}

interface UseProductSelectionsReturn {
  selections: Record<AssociateProductType, Record<string, boolean>>
  newSelections: Record<AssociateProductType, Record<string, boolean>>
  cachedProducts: React.MutableRefObject<{
    [AssociateProductType.Akrochem]: Product[]
    [AssociateProductType.Competitor]: CompetitorProduct[]
  }>
  handleSelectionChange: (
    type: AssociateProductType,
    newSelection: Record<string, boolean>,
    products: AssociatedProduct[]
  ) => void
  handleRemoveProduct: (type: AssociateProductType, id: string) => void
}

export const useProductSelections = ({
  associatedProductIds,
  associatedCompetitorIds,
}: UseProductSelectionsParams): UseProductSelectionsReturn => {
  const [selections, setSelections] = useState<Record<AssociateProductType, Record<string, boolean>>>({
    [AssociateProductType.Akrochem]: idsToTableSelectedRowFormat(associatedProductIds),
    [AssociateProductType.Competitor]: idsToTableSelectedRowFormat(associatedCompetitorIds),
  })

  // Cache product data across tab switches
  const cachedProducts = useRef({
    [AssociateProductType.Akrochem]: [] as Product[],
    [AssociateProductType.Competitor]: [] as CompetitorProduct[],
  })

  // Compute new selections: filter out already-associated items
  const akrochemNew = Object.keys(selections[AssociateProductType.Akrochem])
    .filter((id) => selections[AssociateProductType.Akrochem][id] && !associatedProductIds.includes(Number(id)))
    .reduce(
      (acc, id) => {
        acc[id] = true
        return acc
      },
      {} as Record<string, boolean>
    )

  const competitorNew = Object.keys(selections[AssociateProductType.Competitor])
    .filter((id) => selections[AssociateProductType.Competitor][id] && !associatedCompetitorIds.includes(id))
    .reduce(
      (acc, id) => {
        acc[id] = true
        return acc
      },
      {} as Record<string, boolean>
    )

  const newSelections = {
    [AssociateProductType.Akrochem]: akrochemNew,
    [AssociateProductType.Competitor]: competitorNew,
  }

  /**
   * Handles product selection changes with cache merging strategy.
   *
   * When user searches and selects products, we cache full product data to persist
   * selections across search criteria changes and tab switches. Uses merge strategy:
   * - Preserves previously cached products that remain selected
   * - Adds newly selected products from current search results
   * - Removes products that are no longer selected (filtered out automatically)
   */
  const handleSelectionChange = (
    type: AssociateProductType,
    newSelection: Record<string, boolean>,
    products: AssociatedProduct[]
  ) => {
    setSelections((prev) => ({
      ...prev,
      [type]: newSelection,
    }))

    const selectedIds = new Set(Object.keys(newSelection).filter((id) => newSelection[id]))

    if (type === AssociateProductType.Akrochem) {
      const existingCache = cachedProducts.current[AssociateProductType.Akrochem]
      const preservedProducts = existingCache.filter((p) => selectedIds.has(getProductId(p)))
      const newProducts = (products as Product[]).filter((p) => {
        const id = getProductId(p)
        return selectedIds.has(id) && !preservedProducts.some((existing) => getProductId(existing) === id)
      })
      cachedProducts.current[AssociateProductType.Akrochem] = [...preservedProducts, ...newProducts]
    } else {
      const existingCache = cachedProducts.current[AssociateProductType.Competitor]
      const preservedProducts = existingCache.filter((p) => selectedIds.has(getProductId(p)))
      const newProducts = (products as CompetitorProduct[]).filter((p) => {
        const id = getProductId(p)
        return selectedIds.has(id) && !preservedProducts.some((existing) => getProductId(existing) === id)
      })
      cachedProducts.current[AssociateProductType.Competitor] = [...preservedProducts, ...newProducts]
    }
  }

  const handleRemoveProduct = (type: AssociateProductType, id: string) => {
    setSelections((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [id]: false,
      },
    }))

    if (type === AssociateProductType.Akrochem) {
      cachedProducts.current[AssociateProductType.Akrochem] = cachedProducts.current[
        AssociateProductType.Akrochem
      ].filter((p) => getProductId(p) !== id)
    } else {
      cachedProducts.current[AssociateProductType.Competitor] = cachedProducts.current[
        AssociateProductType.Competitor
      ].filter((p) => getProductId(p) !== id)
    }
  }

  return {
    selections,
    newSelections,
    cachedProducts,
    handleSelectionChange,
    handleRemoveProduct,
  }
}
